import { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Paper, Typography, Tooltip } from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '../contexts/ThemeContext';
import { themes } from '../themes/themeConfig';

/**
 * ThemeSwitcher Component
 * Provides a floating UI control for selecting between available themes
 * Features:
 * - Floating button in bottom-right corner
 * - Theme selector panel with grid of theme options
 * - Visual indication of active theme
 * - Keyboard navigation support (Tab, Enter, Escape)
 * - Smooth transitions and hover effects
 */
const ThemeSwitcher = () => {
  const { currentTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  // Toggle panel open/closed
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Handle theme selection
  const handleThemeSelect = (themeId) => {
    setTheme(themeId);
    setIsOpen(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (event, themeId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleThemeSelect(themeId);
    }
  };

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]); 

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Get array of theme objects
  const themeList = Object.values(themes);

  return (
    <>
      {/* Floating Button */}
      <Tooltip title="Change Theme" placement="left">
        <IconButton
          ref={buttonRef}
          onClick={togglePanel}
          aria-label="Open theme switcher"
          aria-expanded={isOpen}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 56,
            height: 56,
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease',
            zIndex: 1300,
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <PaletteIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>

      {/* Theme Selector Panel */}
      {isOpen && (
        <Paper
          ref={panelRef}
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 88,
            right: 16,
            width: 520,
            maxHeight: '80vh',
            overflowY: 'auto',
            borderRadius: 3,
            padding: 2.5,
            zIndex: 1300,
            animation: 'slideUp 0.3s ease',
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: 2,
              fontWeight: 600,
              //color: 'text.primary',
              fontFamily:'cursive',
              fontSize:'15px'
            }}
          >
            Choose Theme
          </Typography>

          {/* Theme Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}
          >
            {themeList.map((theme) => {
              const isActive = currentTheme === theme.id;

              return (
                <Box
                  key={theme.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleThemeSelect(theme.id)}
                  onKeyDown={(e) => handleKeyDown(e, theme.id)}
                  aria-label={`Select ${theme.name} theme`}
                  aria-pressed={isActive}
                  sx={{
                    position: 'relative',
                    padding: 1.5,
                    borderRadius: 2,
                    border: isActive ? '2px solid' : '2px solid transparent',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    backgroundColor: isActive ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                    '&:focus': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {/* Active Theme Indicator */}
                  {isActive && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        color: 'primary.main',
                        fontSize: 18,
                      }}
                    />
                  )}

                  {/* Theme Icon */}
                  <Box
                    sx={{
                      fontSize: 28,
                      marginBottom: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {theme.icon}
                  </Box>

                  {/* Theme Name */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textAlign: 'center',
                      fontSize: '12px',
                      marginBottom: 0.25,
                      color: 'text.primary',
                      display: 'block',
                    }}
                  >
                    {theme.name}
                  </Typography>

                  {/* Theme Description */}
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: 'text.secondary',
                      display: 'block',
                      fontSize: '10px',
                    }}
                  >
                    {theme.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ThemeSwitcher;
